/**
 * Merge the newest-N payments window with an unbounded deposit fetch into one
 * list, deduped by id and ordered exactly as a single `due_date desc` query
 * would have produced (Postgres sorts DESC NULLS FIRST).
 *
 * The deposit is billed once, at the start of a tenancy, so it carries the
 * oldest due_date on the booking — past ~24 rent rows a single newest-24
 * query scrolls straight past it. Fetching all deposit rows separately and
 * merging here is what keeps the tenant Dashboard's deposit card from going
 * blank on a long tenancy (#1326), without changing the shape anything
 * downstream reads (still one array, same ordering).
 */
export function mergeRecentAndDepositPayments<
  T extends { id?: string | number | null; due_date?: string | null },
>(recentRows: T[], depositRows: T[]): T[] {
  const byId = new Map<string, T>();
  for (const row of [...recentRows, ...depositRows]) {
    if (row?.id != null) byId.set(String(row.id), row);
  }
  const hasDate = (row: T) =>
    row?.due_date != null && String(row.due_date) !== "";
  return Array.from(byId.values()).sort((a, b) => {
    if (!hasDate(a) && !hasDate(b)) return 0;
    if (!hasDate(a)) return -1;
    if (!hasDate(b)) return 1;
    return String(b.due_date).localeCompare(String(a.due_date));
  });
}
